import { useLocalSearchParams, useRouter } from 'expo-router';
import { useEffect } from 'react';
import useAuthStore from '@/stores/useAuthStore';
import useJoinIntentStore, { type JoinKind } from '@/stores/useJoinIntentStore';
import {
  getJoinKindConfiguration,
  resolveJoinMemberType,
} from '@/utils/joinKind';
import normalizeInviteCode from '@/utils/normalizeInviteCode';

export default function InviteRoute({ kind }: { kind: JoinKind }) {
  const { code, memberType } = useLocalSearchParams<{
    code?: string;
    memberType?: string;
  }>();
  const authStatus = useAuthStore((state) => state.status);
  const hasHydratedJoinIntent = useJoinIntentStore(
    (state) => state.hasHydratedJoinIntent,
  );
  const joinIntent = useJoinIntentStore((state) => state.joinIntent);
  const joinResumePending = useJoinIntentStore(
    (state) => state.joinResumePending,
  );
  const markJoinIntentForResume = useJoinIntentStore(
    (state) => state.markJoinIntentForResume,
  );
  const setJoinIntent = useJoinIntentStore((state) => state.setJoinIntent);
  const router = useRouter();
  const normalizedCode = normalizeInviteCode(code || '');
  const normalizedMemberType = resolveJoinMemberType(kind, memberType);

  useEffect(() => {
    if (authStatus === 'loading' || !hasHydratedJoinIntent) return;

    const matchesStoredIntent =
      joinIntent?.kind === kind &&
      joinIntent.code === normalizedCode &&
      joinIntent.memberType === normalizedMemberType;

    if (normalizedCode.length === 8) {
      if (!matchesStoredIntent) {
        setJoinIntent({
          kind,
          code: normalizedCode,
          memberType: normalizedMemberType,
        });
      }

      if (!matchesStoredIntent || !joinResumePending) {
        markJoinIntentForResume();
      }
    }

    if (authStatus === 'out') {
      router.replace({
        pathname: '/sign-in',
        params: normalizedCode.length === 8 ? { invite: 'true' } : {},
      });
      return;
    }

    router.replace(
      {
        pathname: getJoinKindConfiguration(kind).joinPathname,
        params: {
          ...(normalizedCode ? { code: normalizedCode } : {}),
          memberType: normalizedMemberType,
        },
      },
      { withAnchor: true },
    );
  }, [
    authStatus,
    hasHydratedJoinIntent,
    joinIntent,
    joinResumePending,
    kind,
    markJoinIntentForResume,
    normalizedCode,
    normalizedMemberType,
    router,
    setJoinIntent,
  ]);

  return null;
}
